// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useCallback, useMemo } from "react";
import { connect, useDispatch, useSelector } from "react-redux";

import { logout } from "src/actions";
import { setCurrentOrg } from "src/actions/organization";
import NavLinks from "src/components/auth/NavLinks";
import {
  getAllOrgDetails,
  getCurrentUserId,
  getLastOrg,
  getSignInError,
} from "src/reducers";
import Icon from "src/icons";
import { getServerErrorConfig } from "src/constants/errorMessages";
import SingleSelect from "src/components/Unifize/Select/SingleSelect";
import { Button, Flex, Text, useSarah } from "@unifize/sarah";

type Props = {
  logout: Function,
};

const ServerErrorMessage = ({ logout }: Props) => {
  const { colors } = useSarah();
  const uid = useSelector(({ app }) => getCurrentUserId(app));

  const allOrgDetails = useSelector(({ app }) => getAllOrgDetails(app));
  const currentOrgId = useSelector(({ app }) => getLastOrg(app));

  const sendAuthError = useSelector(({ app }) =>
    getSignInError("sendAuthToken")(app),
  );
  const orgError = useSelector(({ app }) => getSignInError("orgFailure")(app));

  const activeError = sendAuthError || orgError;
  const actionType = activeError?.actionType || "UNKNOWN_ERROR";
  const errorConfig = getServerErrorConfig(actionType);

  const dispatch = useDispatch();

  const activeOrgs = useMemo(() => {
    if (!allOrgDetails) return [];

    return allOrgDetails
      .toArray()
      .filter((org) => org.id !== currentOrgId)
      .map((org) => ({
        id: String(org.id),
        title: org.title ?? "",
      }));
  }, [allOrgDetails, currentOrgId]);

  const handleOrgSwitch = useCallback(
    (orgId: string) => {
      if (!orgId) return;
      dispatch(setCurrentOrg(parseInt(orgId, 10), uid, true));
    },
    [uid],
  );

  return (
    <Flex direction="column" justifyContent="center">
      <Flex
        direction="column"
        width="328px"
        background={colors.base.white}
        padding={6}
        style={{
          borderRadius: "4px",
        }}
      >
        <Flex justifyContent="center" style={{ margin: "36px 0" }}>
          <Icon type="serverError" />
        </Flex>
        <Text>{errorConfig.description}</Text>

        <Flex direction="column" width="100%" style={{ marginTop: "8px" }}>
          <SingleSelect
            data={activeOrgs}
            keys={["id", "title"]}
            onChange={handleOrgSwitch}
            selected=""
            placeholder={
              activeOrgs.length > 0 ? "Select org" : "No orgs available"
            }
            isDisabled={activeOrgs.length === 0}
            maxHeight="200px"
          />
        </Flex>

        <Button
          variant="outline"
          onClick={logout}
          style={{ marginTop: "16px" }}
        >
          {i18n.t(k.LOGOUT)}
        </Button>
      </Flex>
      <NavLinks />
    </Flex>
  );
};

export default connect(null, {
  logout,
})(ServerErrorMessage);
